import { Box } from "@rebass/grid";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { useBrokerFeature } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import {
  createApplicationFilterStart,
  deleteApplicationFilterStart,
  updateApplicationFilterStart,
} from "@rentiohq/shared-frontend/dist/reduxV2/applicationFilters/applicationFilters.actions";
import { useGetApplicationFilters } from "@rentiohq/shared-frontend/dist/reduxV2/applicationFilters/applicationFilters.hooks";
import * as applicationActions from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.actions";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import * as propertySelectorsV2 from "@rentiohq/shared-frontend/dist/reduxV2/property/property.selectors";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Can,
  Error,
  ESpacings,
  Loading,
} from "@rentiohq/web-shared/dist/components";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import { orderBy } from "lodash";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useParams } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import { getFlows } from "utils/application.utils";
import { DragAndDropContextProvider } from "./context/DragAndDropContextProvider";
import PropertyDetailApplications from "./PropertyDetailApplications";

export interface IStatusChange {
  id: string;
  step: number;
  tenantFeedbackText?: string;
}

export const PropertyDetailApplicationsContainer = () => {
  const params = useParams<{ propertyId: string }>();
  const propertyId = params.propertyId ? +params.propertyId : undefined;

  const property = useSelector((state: IRootStore) =>
    propertyId ? propertySelectorsV2.getDetail(state, propertyId) : undefined,
  );

  const { broker } = authHooks.useSelf();

  const {
    items: applicationsRaw,
    isFetching,
    fetchError,
    refetch: refetchApplications,
  } = applicationHooks.useGetAll({
    query: {
      filter: { propertyId: { eq: propertyId } },
      sort: [{ field: "providedAt", method: "DESC" }],
    },
    shouldRefetch: true,
  });
  // Memoised because the array identity is a prop all the way down to the
  // memoised table rows; a fresh sort on every render handed every row a new
  // list and made the memoisation buy nothing.
  const applications = useMemo(
    () =>
      applicationsRaw
        ? orderBy(
            applicationsRaw,
            x => {
              return x.stepCompletedAt || x.stepStartedAt || x.providedAt;
            },
            "desc",
          )
        : [],
    [applicationsRaw],
  );

  const { applicationFilters: fetchedFilters = [], refetch: refetchFilters } =
    useGetApplicationFilters();
  const [optimisticFilters, setOptimisticFilters] = useState<any[]>([]);

  const mergedFilters = useMemo(() => {
    const fetchedIds = new Set(fetchedFilters.map(f => f.id));
    const newlyAdded = optimisticFilters.filter(f => !fetchedIds.has(f.id));
    return [...newlyAdded, ...fetchedFilters];
  }, [fetchedFilters, optimisticFilters]);

  const dispatch = useDispatch();
  const hasCalendarSync = useBrokerFeature(EBrokerFeature.CalendarSync);
  const intakeType = broker?.intakeType || 2;
  const steps = getFlows(intakeType, hasCalendarSync);

  if (!applications || applications.length === 0) {
    if (isFetching) {
      return (
        <Box mb={ESpacings.base}>
          <Loading />
        </Box>
      );
    }

    if (fetchError) {
      return <Error errors={[fetchError]} />;
    }
  }

  const fillApplicantsName = (applicationId: string, text?: string) => {
    if (text) {
      const application = applications.find(
        application => application.id === applicationId,
      );
      const firstContact = application?.contacts?.[0];
      return text.replace(
        "{{applicants}}",
        firstContact ? getName(firstContact) : "applicant",
      );
    }

    return text;
  };

  const handleStatusChange = async (params: IStatusChange) => {
    const { id: applicationId, step: stepString, tenantFeedbackText } = params;
    const step = Number(stepString);

    dispatch(
      applicationActions.updateApplicationStepStart.getAction({
        id: applicationId,
        step,
        tenantFeedbackText: fillApplicantsName(
          applicationId,
          tenantFeedbackText,
        ),
        onSuccess:
          step === 3
            ? () => {
                toast({
                  heading: getLocalizedText(
                    "property.applications.availability.calendar_deactivated",
                  ),
                  variation: "success",
                });
              }
            : undefined,
      }),
    );
  };

  const handleSaveFilter = (
    name: string,
    criteria: any,
    onSuccess?: (filter: any) => void,
  ) => {
    dispatch(
      createApplicationFilterStart.getAction({
        data: { name, criteria },
        onSuccess: filter => {
          const newFilter = filter?.data || filter;
          setOptimisticFilters(prev => [newFilter, ...prev]);
          refetchFilters();
          onSuccess?.(newFilter);
        },
      }),
    );
  };

  const handleUpdateFilter = (id: number, name: string, criteria: any) => {
    dispatch(
      updateApplicationFilterStart.getAction({
        id,
        data: { name, criteria },
        onSuccess: () => refetchFilters(),
      }),
    );
  };

  const handleDeleteFilter = (id: number) => {
    dispatch(
      deleteApplicationFilterStart.getAction({
        id,
        onSuccess: () => {
          // Also drop it from the optimistic list, otherwise a just-created
          // filter reappears once it falls out of the refetched set.
          setOptimisticFilters(prev => prev.filter(f => f.id !== id));
          refetchFilters();
        },
      }),
    );
  };

  if (!property) {
    return null;
  }

  // We're using a Context here. That's not a pattern we use a lot, but it's
  // useful here because we need the drag & drop info in almost all components lower in the hierarchy,
  // but that info doesn't need to be stored in Redux (not used anywhere else)
  const renderContent = () => (
    <DragAndDropContextProvider>
      <PropertyDetailApplications
        applications={applications || []}
        refetchApplications={refetchApplications}
        handleOneStatusChange={handleStatusChange}
        type={intakeType}
        visitorSteps={steps.visitorSteps}
        candidateSteps={steps.candidateSteps}
        notEligibleSteps={steps.notEligibleSteps}
        property={property}
        savedFilters={mergedFilters}
        onSaveFilter={handleSaveFilter}
        onUpdateFilter={handleUpdateFilter}
        onDeleteFilter={handleDeleteFilter}
      />
    </DragAndDropContextProvider>
  );

  return (
    <Can
      role={property.roles}
      perform="propertyApplications:visit"
      yes={renderContent}
      no={() => <Navigate to={`/properties/${propertyId}`} />}
    />
  );
};
